import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useUploadQueue } from '../hooks/useUploadQueue';
import {
  describeUploadQueue,
  discardFailedUploads,
  retryFailedUploads,
  uploadedLabel,
} from '../services/uploadQueue';

interface UploadQueueIndicatorProps {
  /** The camera is fullscreen and shows its own per-shot status. */
  hidden?: boolean;
}

/**
 * App-wide pill for the background photo upload queue.
 *
 * Lives in Layout so it follows the user off the project that started the
 * uploads. Sits bottom-left, clear of the FAB, and only renders while there is
 * something to report: photos in flight, a batch that just finished (shown for
 * a moment), or failures that need a decision.
 *
 * Failure is amber, not red: nothing is lost — the photos are still stored on
 * the phone and can be retried — and DESIGN.md reserves red for destruction.
 */
const UploadQueueIndicator: React.FC<UploadQueueIndicatorProps> = ({ hidden = false }) => {
  const queue = useUploadQueue();
  const [doneCount, setDoneCount] = useState(0);
  const prevPending = useRef(queue.pendingCount);

  // Flash "N foton uppladdade" once a batch drains.
  useEffect(() => {
    const wasPending = prevPending.current > 0;
    prevPending.current = queue.pendingCount;
    if (!wasPending || queue.pendingCount > 0 || queue.completedInBatch === 0) return;
    setDoneCount(queue.completedInBatch);
    const timer = window.setTimeout(() => setDoneCount(0), 3000);
    return () => window.clearTimeout(timer);
  }, [queue.pendingCount, queue.completedInBatch]);

  if (hidden) return null;

  const showDone =
    doneCount > 0 && queue.pendingCount === 0 && queue.completedInBatch === doneCount;
  const text = describeUploadQueue(queue);

  let content: React.ReactNode = null;
  if (queue.pendingCount > 0) {
    content = (
      <>
        <Loader2 size={16} className="animate-spin text-accent-600 dark:text-accent-400 flex-shrink-0" aria-hidden="true" />
        <span>{text}</span>
      </>
    );
  } else if (queue.failedCount > 0) {
    content = (
      <>
        <AlertCircle size={16} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
        <span>{text}</span>
        <button
          type="button"
          onClick={retryFailedUploads}
          className="min-h-[44px] px-2 text-accent-600 dark:text-accent-400 font-semibold active:opacity-50 transition-opacity"
        >
          Försök igen
        </button>
        <button
          type="button"
          onClick={discardFailedUploads}
          className="min-h-[44px] px-2 text-[var(--text-secondary)] active:opacity-50 transition-opacity"
        >
          Ta bort
        </button>
      </>
    );
  } else if (showDone) {
    content = (
      <>
        <Check size={16} className="text-emerald-500 flex-shrink-0" aria-hidden="true" />
        <span>{uploadedLabel(doneCount)}</span>
      </>
    );
  }

  if (!content) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-4 right-[5.5rem] lg:left-[16rem] lg:right-24 lg:bottom-6 z-30 pointer-events-none"
      style={{ bottom: 'calc(49px + env(safe-area-inset-bottom, 0px) + 16px)' }}
    >
      <div className="pointer-events-auto w-fit max-w-full min-h-[44px] flex items-center gap-2 rounded-full bg-[var(--bg-surface)] border border-[var(--border-default)] shadow-ios-elevated px-4 text-[13px] font-medium text-[var(--text-primary)] animate-slide-up">
        {content}
      </div>
    </div>
  );
};

export default UploadQueueIndicator;
